import { router } from "@inertiajs/react"
import { Loader2 } from "lucide-react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { identityEmailVerificationPath } from "@/routes"

/**
 * ResendVerificationButton — OKS-146.
 *
 * Single-button trigger that POSTs `identityEmailVerificationPath()`
 * via `router.post(...)` (the Inertia router — NOT a wrapper `<Form>`).
 *
 * Why router.post (not `<Form>`):
 *   - The resend has zero user-input fields; the only payload is the
 *     authenticated session cookie. `<Form>` adds tokenisation
 *     overhead for nothing AND inserts a `<form>` element into the
 *     DOM, which conflicts when the consuming page (Settings → Email)
 *     ALREADY hosts an Inertia `<Form>` for the email-change PATCH —
 *     HTML disallows nested `<form>` elements, the inner submit
 *     handler doesn't survive the browser's tokeniser, and the
 *     Inertia visit silently degrades to a native HTML form submit
 *     (no `X-Inertia` header → full page reload → `onSuccess` never
 *     fires → cooldown state machine never engages).
 *   - `router.post` is the canonical Inertia-2.x escape hatch for
 *     button-only submits and matches the existing CTAButton pattern
 *     in `collab-card/cta-button.tsx`.
 *
 * State machine (3 states):
 *
 *   - `idle`     → enabled; label = "Resend verification".
 *   - `sending`  → disabled; spinner + "Sending…" while the visit
 *                  is in flight.
 *   - `cooldown` → disabled; "Sent ✓ Resend in {mm:ss}" countdown,
 *                  decrementing each second. After 60s → back to `idle`.
 *
 * Cooldown is component-local non-persistent state per UX Spec §2.2.
 * Server-side cooldown (Solid Cache key, 60s TTL) is the source of
 * truth — bypass attempts are rejected with `flash[:error]` which
 * `useFlash` renders as a Sonner toast. UX accepts that a user who
 * rage-reloads sees the timer reset locally; the server still rejects.
 *
 * `preserveState: true` on the visit keeps THIS component's React
 * tree alive across the controller's 303 redirect to /settings/email,
 * so the `cooldownRemaining` state survives the visit and the button
 * re-renders in the cooldown state. Without it, Inertia would remount
 * the page tree and reset every useState.
 */

const COOLDOWN_SECONDS = 60

interface ResendVerificationButtonProps {
  /** Optional shadcn variant override; defaults to `outline`. */
  variant?: "default" | "outline" | "secondary" | "ghost"
  /**
   * Optional shadcn size override; defaults to `default`.
   *
   * OKS-874 — was `sm` (h-8, 32px). This is a creator-facing CTA on the
   * unverified-email path and the collab gate, so it takes the 44px mobile
   * floor like any other. NOTE: changing this default only reaches call
   * sites that DON'T pass `size` — the two that passed `size="sm"`
   * explicitly had to drop the prop separately (a default parameter cannot
   * reach an explicit override).
   */
  size?: "default" | "sm" | "lg"
  className?: string
  /**
   * `data-testid` for the rendered button. Defaults to
   * `"resend-verification-button"` (the Settings → Email usage). The
   * creator-shell `UnverifiedEmailBanner` reuses this component AND
   * lives above every creator page — including Settings → Email —
   * so an unverified creator there renders TWO instances. Overriding
   * the testid (banner passes `"unverified-email-banner-action"`)
   * keeps `getByTestId` unambiguous on the shared page (OKS-647).
   */
  testId?: string
}

export function ResendVerificationButton({
  variant = "outline",
  size = "default",
  className,
  testId = "resend-verification-button",
}: ResendVerificationButtonProps) {
  const { t } = useTranslation()
  const [cooldownRemaining, setCooldownRemaining] = useState<number>(0)
  const [processing, setProcessing] = useState<boolean>(false)

  // Decrement once per second while in cooldown. Cleared on unmount,
  // not persisted across navigation (Inertia full reloads will reset
  // the timer — server cooldown is the durable lock).
  useEffect(() => {
    if (cooldownRemaining <= 0) return
    const handle = window.setInterval(() => {
      setCooldownRemaining((s) => Math.max(0, s - 1))
    }, 1000)
    return () => window.clearInterval(handle)
  }, [cooldownRemaining])

  function handleClick() {
    if (processing || cooldownRemaining > 0) return
    setProcessing(true)
    router.post(
      identityEmailVerificationPath(),
      {},
      {
        // Keeps the React tree alive across the 303 redirect; without
        // it Inertia remounts the page and the cooldown state would
        // reset to 0 (the state machine would never enter `cooldown`).
        preserveState: true,
        preserveScroll: true,
        onSuccess: () => setCooldownRemaining(COOLDOWN_SECONDS),
        onFinish: () => setProcessing(false),
      },
    )
  }

  const inCooldown = cooldownRemaining > 0
  const disabled = processing || inCooldown
  const dataState = processing ? "sending" : inCooldown ? "cooldown" : "idle"

  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      onClick={handleClick}
      disabled={disabled}
      aria-disabled={disabled}
      data-testid={testId}
      data-state={dataState}
      className={className}
      title={
        inCooldown
          ? t("email_verification.resend.cooldown_aria", {
              remaining: formatRemaining(cooldownRemaining),
            })
          : undefined
      }
    >
      {processing && <Loader2 className="animate-spin" aria-hidden />}
      {renderLabel({ processing, inCooldown, cooldownRemaining, t })}
    </Button>
  )
}

interface RenderLabelArgs {
  processing: boolean
  inCooldown: boolean
  cooldownRemaining: number
  t: (key: string, opts?: Record<string, unknown>) => string
}

function renderLabel({
  processing,
  inCooldown,
  cooldownRemaining,
  t,
}: RenderLabelArgs) {
  if (processing) return t("email_verification.resend.sending")
  if (inCooldown) {
    return (
      <span aria-live="polite" data-testid="resend-verification-cooldown">
        {t("email_verification.resend.cooldown", {
          remaining: formatRemaining(cooldownRemaining),
        })}
      </span>
    )
  }
  return t("email_verification.resend.idle")
}

/**
 * Format remaining seconds as `m:ss` (e.g. 60 → "1:00", 5 → "0:05").
 * Always two-digit seconds; minutes elide leading zero for the
 * single-minute V1 cooldown.
 */
function formatRemaining(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${s.toString().padStart(2, "0")}`
}
